/**
 * INYRON HIGH-ORDER HARMONIC FIELD
 *
 * Deterministic, bounded spectral synthesis for gameplay-facing modulation.
 * It models high-order harmonics, beat envelopes and intermodulation while
 * enforcing Nyquist/order budgets so it can run safely in real time.
 */

export type HarmonicQuality = 'low' | 'medium' | 'high' | 'ultra'

export interface HarmonicFieldConfig {
  identity: string
  fundamentalHz: number
  maxOrder: number
  sampleRateHz: number
  rolloff: number
  detuneRatio: number
  quality: HarmonicQuality
}

export interface HarmonicFieldInput {
  energy01: number
  tension01: number
  coherence01: number
  detail01: number
  distance01: number
  coupling01: number
  phaseOffset01: number
}

export interface HarmonicBands {
  low: number
  mid: number
  high: number
  ultra: number
}

export interface HarmonicFieldFrame {
  fundamentalHz: number
  orderCount: number
  waveform: number
  beatEnvelope: number
  intermodulation: number
  phaseCoherence: number
  spectralCentroid01: number
  bands: HarmonicBands
  motionPulse: number
  cameraImpulse: number
  vfxEmission: number
  audioBrightness: number
  physicsResponse: number
  aiArousal: number
  signature: string
}

const TAU = Math.PI * 2
const QUALITY_ORDER: Record<HarmonicQuality, number> = {
  low: 8,
  medium: 16,
  high: 32,
  ultra: 64,
}

const clamp = (n: number, lo = 0, hi = 1): number =>
  Math.max(lo, Math.min(hi, Number.isFinite(n) ? n : lo))

const signed = (n: number): number =>
  Math.max(-1, Math.min(1, Number.isFinite(n) ? n : 0))

const fract = (n: number): number => n - Math.floor(n)

function hash32(input: string): number {
  let h = 2166136261 >>> 0
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  h ^= h >>> 16
  h = Math.imul(h, 0x7feb352d)
  h ^= h >>> 15
  h = Math.imul(h, 0x846ca68b)
  h ^= h >>> 16
  return h >>> 0
}

function unitHash(seed: number, index: number): number {
  let x = (seed ^ Math.imul(index + 0x9e3779b9, 0x85ebca6b)) >>> 0
  x ^= x >>> 16
  x = Math.imul(x, 0x7feb352d)
  x ^= x >>> 15
  x = Math.imul(x, 0x846ca68b)
  x ^= x >>> 16
  return (x >>> 0) / 4294967295
}

function quant(n: number): string {
  return Math.round(n * 10000).toString(36)
}

function defaultConfig(identity: string): HarmonicFieldConfig {
  const seed = hash32(identity || 'inyron-harmonic')
  return {
    identity,
    fundamentalHz: 0.72 + unitHash(seed, 1) * 1.28,
    maxOrder: 64,
    sampleRateHz: 240,
    rolloff: 1.18 + unitHash(seed, 2) * 0.34,
    detuneRatio: 0.0075 + unitHash(seed, 3) * 0.0125,
    quality: 'high',
  }
}

export function createHarmonicFieldConfig(
  identity: string,
  overrides: Partial<Omit<HarmonicFieldConfig, 'identity'>> = {},
): HarmonicFieldConfig {
  const base = defaultConfig(identity)
  return {
    ...base,
    ...overrides,
    identity,
    fundamentalHz: Math.max(0.05, overrides.fundamentalHz ?? base.fundamentalHz),
    maxOrder: Math.max(1, Math.floor(overrides.maxOrder ?? base.maxOrder)),
    sampleRateHz: Math.max(8, overrides.sampleRateHz ?? base.sampleRateHz),
    rolloff: Math.max(0.55, overrides.rolloff ?? base.rolloff),
    detuneRatio: clamp(overrides.detuneRatio ?? base.detuneRatio, 0.0005, 0.08),
    quality: overrides.quality ?? base.quality,
  }
}

export class HarmonicFieldRuntime {
  readonly config: HarmonicFieldConfig
  private readonly seed: number

  constructor(
    config: HarmonicFieldConfig | string,
    overrides: Partial<Omit<HarmonicFieldConfig, 'identity'>> = {},
  ) {
    this.config = typeof config === 'string'
      ? createHarmonicFieldConfig(config, overrides)
      : config
    this.seed = hash32(this.config.identity)
  }

  sample(timeS: number, input: Partial<HarmonicFieldInput> = {}): HarmonicFieldFrame {
    const t = Math.max(0, Number.isFinite(timeS) ? timeS : 0)
    const energy = clamp(input.energy01 ?? 0.25)
    const tension = clamp(input.tension01 ?? 0.15)
    const coherence = clamp(input.coherence01 ?? 0.7)
    const detail = clamp(input.detail01 ?? 0.6)
    const distance = clamp(input.distance01 ?? 0)
    const coupling = clamp(input.coupling01 ?? 0.35)
    const phaseOffset = fract(input.phaseOffset01 ?? 0)

    const fundamentalHz = this.config.fundamentalHz * (
      0.78 + energy * 0.58 + tension * 0.12
    )
    const nyquistOrder = Math.max(
      1,
      Math.floor((this.config.sampleRateHz * 0.5) / fundamentalHz),
    )
    const qualityCap = QUALITY_ORDER[this.config.quality]
    const detailOrder = Math.max(
      4,
      Math.round(4 + detail * (qualityCap - 4) * (1 - distance * 0.82)),
    )
    const orderCount = Math.max(
      1,
      Math.min(this.config.maxOrder, qualityCap, detailOrder, nyquistOrder),
    )

    let weighted = 0
    let weightSum = 0
    let centroid = 0
    let magnitudeSum = 0
    let low = 0
    let mid = 0
    let high = 0
    let ultra = 0
    let lowW = 0
    let midW = 0
    let highW = 0
    let ultraW = 0

    for (let n = 1; n <= orderCount; n += 1) {
      const phaseSeed = unitHash(this.seed, n * 17 + 5)
      const phase = TAU * (
        fundamentalHz * n * t +
        phaseSeed +
        phaseOffset * (0.35 + n * 0.017)
      )
      const oddEvenBias = n % 2 === 0
        ? 1 - tension * 0.18
        : 1 + tension * 0.22
      const amplitude = (
        Math.pow(n, -this.config.rolloff) *
        oddEvenBias *
        (0.72 + coherence * 0.28)
      )
      const value = Math.sin(phase) * amplitude
      weighted += value
      weightSum += amplitude

      const magnitude = Math.abs(value)
      centroid += (n / orderCount) * magnitude
      magnitudeSum += magnitude

      const ratio = n / orderCount
      if (ratio <= 0.125) {
        low += value
        lowW += amplitude
      } else if (ratio <= 0.375) {
        mid += value
        midW += amplitude
      } else if (ratio <= 0.7) {
        high += value
        highW += amplitude
      } else {
        ultra += value
        ultraW += amplitude
      }
    }

    const waveform = signed(weighted / Math.max(0.000001, weightSum))
    const bands: HarmonicBands = {
      low: signed(low / Math.max(0.000001, lowW)),
      mid: signed(mid / Math.max(0.000001, midW)),
      high: signed(high / Math.max(0.000001, highW)),
      ultra: signed(ultra / Math.max(0.000001, ultraW)),
    }

    const detunedHz = fundamentalHz * (
      1 + this.config.detuneRatio * (0.65 + tension * 0.7)
    )
    const beatEnvelope = signed(
      Math.sin(TAU * Math.abs(detunedHz - fundamentalHz) * t + phaseOffset * TAU),
    )
    const mixHz = Math.max(
      0.01,
      Math.abs(2 * detunedHz - fundamentalHz),
    )
    const intermodulation = signed(
      Math.sin(TAU * mixHz * t + unitHash(this.seed, 313) * TAU) *
      coupling,
    )

    const spectralCentroid01 = clamp(
      magnitudeSum > 0 ? centroid / magnitudeSum : 0,
    )
    const phaseCoherence = clamp(
      coherence * (0.82 + 0.18 * (1 - Math.abs(bands.ultra))),
    )

    const motionPulse = signed(
      waveform * 0.58 +
      bands.low * 0.2 +
      beatEnvelope * 0.14 +
      intermodulation * 0.08,
    ) * energy

    const cameraImpulse = signed(
      bands.high * 0.42 +
      bands.ultra * 0.34 +
      intermodulation * 0.24,
    ) * energy * energy

    const vfxEmission = clamp(
      0.08 +
      energy * 0.5 +
      Math.abs(bands.high) * 0.16 +
      Math.abs(bands.ultra) * 0.12 +
      Math.abs(intermodulation) * 0.14,
    )

    const audioBrightness = clamp(
      spectralCentroid01 * 0.7 +
      Math.abs(bands.ultra) * 0.18 +
      tension * 0.12,
    )

    const physicsResponse = signed(
      bands.low * 0.52 +
      bands.mid * 0.28 +
      intermodulation * 0.2,
    ) * energy

    const aiArousal = clamp(
      energy * 0.55 +
      tension * 0.22 +
      Math.abs(beatEnvelope) * 0.1 +
      Math.abs(intermodulation) * 0.13,
    )

    const signature = [
      this.seed.toString(36),
      orderCount.toString(36),
      quant(fundamentalHz),
      quant(waveform),
      quant(beatEnvelope),
      quant(intermodulation),
      quant(spectralCentroid01),
      quant(phaseCoherence),
    ].join(':')

    return {
      fundamentalHz,
      orderCount,
      waveform,
      beatEnvelope,
      intermodulation,
      phaseCoherence,
      spectralCentroid01,
      bands,
      motionPulse,
      cameraImpulse,
      vfxEmission,
      audioBrightness,
      physicsResponse,
      aiArousal,
      signature,
    }
  }
}
